import { toDecimalString } from '../quantity/decimal.js';
import { normalizeUnit } from '../units/normalize.js';
import { foldText } from '../text/normalize.js';
import { EMPTY_NUTRITION, completeEnergy } from './nutrition.js';
import { isNutriScoreLetter } from './nutriscore.js';
/**
 * Parser for Open Food Facts product responses (API v2). Everything in there is untrusted
 * third-party data: lengths are limited, numbers are validated, unknown shapes yield null.
 * No I/O – the API's provider adapter does the fetching.
 */
export const OPEN_FOOD_FACTS_FIELDS = [
    'code',
    'product_name',
    'product_name_de',
    'product_name_en',
    'generic_name',
    'brands',
    'quantity',
    'nutriments',
    'nutrition_data_per',
    'nutriscore_grade',
    'nova_group',
    'ecoscore_grade',
    'ingredients_text',
    'ingredients_text_de',
    'ingredients_text_en',
    'allergens_tags',
    'additives_tags',
    'labels_tags',
    'categories_tags',
    'image_front_url',
    'image_front_small_url',
].join(',');
function text(value, max) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.replace(/\s+/g, ' ').trim();
    return trimmed ? trimmed.slice(0, max) : null;
}
/** "en:gluten" → "gluten", "en:e330" → "E330". */
function tags(value, max, upper = false) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        const tag = text(raw, 100);
        if (!tag)
            continue;
        const name = tag.replace(/^[a-z]{2}:/, '').replace(/-/g, ' ');
        const label = upper ? name.toUpperCase().replace(/\s+/g, '') : name;
        if (label && !out.includes(label))
            out.push(label.slice(0, 80));
        if (out.length >= max)
            break;
    }
    return out;
}
function nutrient(nutriments, key) {
    const raw = nutriments[`${key}_100g`];
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n < 0 || n > 100000)
        return null;
    return toDecimalString(Math.round(n * 1000) / 1000);
}
/** "400 g", "1,5 l", "6 x 0.33 l" → quantity and unit; anything else stays text only. */
export function parsePackageQuantity(raw) {
    if (!raw)
        return { quantity: null, unitCode: null, unitText: null };
    const multi = /^(\d+)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*([a-zA-Zµ]+)\b/.exec(raw);
    const single = /^(\d+(?:[.,]\d+)?)\s*([a-zA-Zµ]+)\b/.exec(raw);
    const amount = multi
        ? Number(multi[1]) * Number(multi[2].replace(',', '.'))
        : single
            ? Number(single[1].replace(',', '.'))
            : NaN;
    const unitRaw = multi ? multi[3] : single ? single[2] : null;
    if (!Number.isFinite(amount) || amount <= 0 || !unitRaw)
        return { quantity: null, unitCode: null, unitText: raw.slice(0, 40) };
    const unit = normalizeUnit(unitRaw);
    return {
        quantity: toDecimalString(Math.round(amount * 1000) / 1000),
        unitCode: unit?.code ?? null,
        unitText: unit ? null : unitRaw.slice(0, 40),
    };
}
function safeImageUrl(value) {
    const raw = text(value, 500);
    if (!raw)
        return null;
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' ? url.toString() : null;
    }
    catch {
        return null;
    }
}
/** Returns null when the response does not describe a usable product. */
export function parseOpenFoodFactsProduct(json, locale = 'de') {
    const body = json;
    if (!body || typeof body !== 'object')
        return null;
    if (body.status !== 1 && body.status !== 'success')
        return null;
    const p = body.product;
    if (!p || typeof p !== 'object')
        return null;
    const barcode = text(p['code'], 14);
    if (!barcode || !/^\d{8,14}$/.test(barcode))
        return null;
    const name = text(p[`product_name_${locale}`], 200) ??
        text(p['product_name'], 200) ??
        text(p['product_name_en'], 200) ??
        text(p['generic_name'], 200);
    if (!name)
        return null;
    const pack = parsePackageQuantity(text(p['quantity'], 60));
    const nutriments = (p['nutriments'] ?? {});
    const values = completeEnergy({
        ...EMPTY_NUTRITION,
        energyKj: nutrient(nutriments, 'energy-kj'),
        energyKcal: nutrient(nutriments, 'energy-kcal'),
        fat: nutrient(nutriments, 'fat'),
        saturatedFat: nutrient(nutriments, 'saturated-fat'),
        carbohydrates: nutrient(nutriments, 'carbohydrates'),
        sugars: nutrient(nutriments, 'sugars'),
        fibre: nutrient(nutriments, 'fiber'),
        protein: nutrient(nutriments, 'proteins'),
        salt: nutrient(nutriments, 'salt'),
    });
    const hasNutrition = Object.values(values).some((v) => v !== null);
    const grade = typeof p['nutriscore_grade'] === 'string' ? p['nutriscore_grade'].toUpperCase() : '';
    const nova = Number(p['nova_group']);
    const eco = text(p['ecoscore_grade'], 1)?.toUpperCase() ?? null;
    return {
        barcode,
        name,
        brand: text(p['brands'], 200)?.split(',')[0]?.trim().slice(0, 120) ?? null,
        packageQuantity: pack.quantity,
        packageUnitCode: pack.unitCode,
        packageUnitText: pack.unitText,
        ingredientsText: text(p[`ingredients_text_${locale}`], 4000) ?? text(p['ingredients_text'], 4000),
        allergens: tags(p['allergens_tags'], 40),
        additives: tags(p['additives_tags'], 80, true),
        labels: tags(p['labels_tags'], 40),
        categories: tags(p['categories_tags'], 40),
        nutriScore: isNutriScoreLetter(grade) ? grade : null,
        novaGroup: Number.isInteger(nova) && nova >= 1 && nova <= 4 ? nova : null,
        ecoScore: eco && /^[A-E]$/.test(eco) ? eco : null,
        nutrition: hasNutrition
            ? { ...values, basis: p['nutrition_data_per'] === '100ml' ? 'PER_100_ML' : 'PER_100_G' }
            : null,
        imageUrl: safeImageUrl(p['image_front_url'] ?? p['image_front_small_url']),
    };
}
const DIET_BLOCKERS = {
    VEGAN: [
        'milch',
        'milk',
        'butter',
        'sahne',
        'cream',
        'kase',
        'cheese',
        'molke',
        'whey',
        'ei',
        'eier',
        'eigelb',
        'vollei',
        'egg',
        'honig',
        'honey',
        'gelatine',
        'gelatin',
        'fleisch',
        'meat',
        'fisch',
        'fish',
        'speck',
        'schwein',
        'pork',
        'rind',
        'beef',
        'huhn',
        'chicken',
    ],
    VEGETARIAN: [
        'gelatine',
        'gelatin',
        'fleisch',
        'meat',
        'fisch',
        'fish',
        'speck',
        'schwein',
        'pork',
        'rind',
        'beef',
        'huhn',
        'chicken',
        'sardellen',
        'anchov',
    ],
    PESCATARIAN: ['fleisch', 'meat', 'speck', 'schwein', 'pork', 'rind', 'beef', 'huhn', 'chicken'],
    DAIRY_FREE: ['milch', 'milk', 'butter', 'sahne', 'cream', 'kase', 'cheese', 'molke', 'whey'],
    EGG_FREE: ['ei', 'eier', 'eigelb', 'eiweiss', 'vollei', 'egg', 'eggs'],
    NUT_FREE: ['nuss', 'nusse', 'nuts', 'mandel', 'almond', 'haselnuss', 'hazelnut', 'walnuss'],
    SOY_FREE: ['soja', 'soy', 'soya'],
    GLUTEN_FREE: [
        'weizen',
        'wheat',
        'gluten',
        'roggen',
        'rye',
        'gerste',
        'barley',
        'dinkel',
        'spelt',
    ],
    LACTOSE_FREE: ['milch', 'milk', 'laktose', 'lactose', 'sahne', 'cream', 'molke', 'whey'],
};
/**
 * Matches between what is printed on a package and the user's settings. Word-based so that
 * "Ei" does not match "Reis". The result is a hint to read the label, never a guarantee.
 */
export function findLabelConflicts(label, preferences) {
    const haystack = foldText(`${label.ingredientsText ?? ''} ${label.allergens.join(' ')}`);
    if (!haystack.trim())
        return [];
    const words = new Set(haystack.split(/[^a-z0-9]+/).filter(Boolean));
    const hasWord = (needle) => {
        const key = foldText(needle).trim();
        if (!key)
            return false;
        if (key.includes(' '))
            return haystack.includes(key);
        for (const word of words) {
            // Plural and compound endings ("erdnuesse", "erdnussoel") still count.
            if (word === key || (key.length >= 4 && word.startsWith(key)))
                return true;
            if (key.length >= 5 && word.startsWith(key.slice(0, -1)))
                return true;
        }
        return false;
    };
    const out = [];
    for (const avoided of preferences.avoidedIngredients)
        if (hasWord(avoided))
            out.push({ term: avoided, kind: 'AVOIDED_INGREDIENT' });
    for (const preference of preferences.dietaryPreferences) {
        const blockers = DIET_BLOCKERS[preference.toUpperCase()] ?? [];
        if (blockers.some((b) => hasWord(b)))
            out.push({ term: preference, kind: 'DIETARY_PREFERENCE' });
    }
    return out;
}
//# sourceMappingURL=open-food-facts.js.map